import { streamingRemoteFeedback, streamingResponseText, streamingFieldText, streamingFeedbackText } from "../i18n/streamingFeedbackPresentation";
import { type StreamingMessage, useStreamingLanguage, streamMessage, streamLocal, streamText } from "../i18n/streamingSettingsPresentation";
/**
 * useWSDSettings — Hook for WSD (What's Streaming Dashboard) settings management.
 *
 * Provides CRUD operations for streaming settings:
 * - Fetch current settings
 * - Update settings (Emby/Jellyfin URLs, API keys, poll interval)
 * - Test media server connections
 */
import { useCallback, useEffect, useState } from "react";

import { getCsrfToken } from "../lib/csrf";
import { putStreamingSettings } from "../lib/streaming/putStreamingSettings";
import { invalidateSystemAppsCache } from "./useSystemApps";
import { toast } from "../lib/toast";
import { getServerLabel } from "../utils/serverDisplay";
import { formatSettingsError } from "../utils/settingsError";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type MediaServerType = "emby" | "jellyfin" | "plex";

/**
 * Per-server-type feature capability flags.
 * `true`  = feature is available; `false` = not supported on this server type.
 * Sourced from `PLEX_UNSUPPORTED` on the backend and injected into every
 * `/api/streaming/settings` response so the UI can gate buttons contract-first.
 */
export interface ServerTypeCapabilities {
  sendMessage: boolean;
  userPolicy: boolean;
  devicePolicies: boolean;
  deviceRename: boolean;
  pluginConfiguration: boolean;
  /** When `false`, terminate may 401 — surface tooltip, not a hidden button */
  terminateRequiresAdminSession: boolean;
}

export type ServerCapabilitiesMap = Record<MediaServerType, ServerTypeCapabilities>;

/** IPTVBoss guide (EPG) availability + freshness for the settings status line. */
export interface IptvbossGuideStatus {
  /** The admin toggle (default on). */
  enabled: boolean;
  /** IPTVBoss is installed on the box. */
  installed: boolean;
  /** At least one XMLTV export file was discovered. */
  exportFound: boolean;
  /** Distinct channels in the current export index. */
  channelCount: number;
  /** Programmes retained in the current guide window. */
  programmeCount: number;
  /** Newest export file mtime (epoch ms), or null. */
  generatedAt: number | null;
}

/** Settings DTO returned by the API (excludes API keys) */
export interface WSDSettingsDTO {
  enabled: boolean;
  pollInterval: number;
  geoIpEnabled: boolean;
  embyEnabled: boolean;
  embyUrl: string;
  embyExternalUrl: string;
  embyExternalUrlDefault: string;
  embyConfigured: boolean;
  /** User(s) who own an Emby install, box-wide (deduplicated, sorted). */
  embyInstalledFor: string[];
  jellyfinEnabled: boolean;
  jellyfinUrl: string;
  jellyfinExternalUrl: string;
  jellyfinExternalUrlDefault: string;
  jellyfinConfigured: boolean;
  /** User(s) who own a Jellyfin install, box-wide (deduplicated, sorted). */
  jellyfinInstalledFor: string[];
  plexEnabled: boolean;
  plexUrl: string;
  plexExternalUrl: string;
  plexConfigured: boolean;
  /** User(s) who own a Plex install, box-wide (deduplicated, sorted). */
  plexInstalledFor: string[];
  /** Dispatcharr base URL used to override Plex Live TV channel/program data. Empty when unset. */
  dispatcharrBaseUrl: string;
  /** `true` when both a Dispatcharr base URL and API key are stored. The API key itself is a secret and never returned. */
  dispatcharrConfigured: boolean;
  /** IPTVBoss guide (EPG) source status — availability, counts, freshness. */
  guideIptvboss: IptvbossGuideStatus;
  /** The admin toggle for Live TV programme-segment analytics (default off). */
  liveTvEnrichmentEnabled: boolean;
  /**
   * Live TV enrichment availability + effective state. `available` reflects the
   * providers present on the box; `effective = enabled && (iptvboss || dispatcharr)`
   * and gates both segment capture and the Top Titles / Trending re-admission.
   */
  liveTvEnrichment: {
    available: { iptvboss: boolean; dispatcharr: boolean };
    effective: boolean;
    segmentCount: number;
  };
  /** Per-server-type capability map. Gate UI buttons by reading this, not by hardcoding `serverType === "plex"`. */
  serverCapabilities: ServerCapabilitiesMap;
}

/** Settings update payload */
export interface WSDSettingsUpdate {
  enabled?: boolean;
  pollInterval?: number;
  geoIpEnabled?: boolean;
  embyEnabled?: boolean;
  embyUrl?: string;
  embyApiKey?: string;
  embyExternalUrl?: string;
  jellyfinEnabled?: boolean;
  jellyfinUrl?: string;
  jellyfinApiKey?: string;
  jellyfinExternalUrl?: string;
  plexEnabled?: boolean;
  plexUrl?: string;
  plexApiKey?: string;
  plexExternalUrl?: string;
  /** Dispatcharr base URL. Send a URL or `""` to clear. */
  dispatcharrBaseUrl?: string;
  /** Dispatcharr API key (secret, write-only). Send only when replacing; `""` clears the stored key. */
  dispatcharrApiKey?: string;
  /** Toggle the IPTVBoss guide (EPG) source on/off. */
  guideIptvbossEnabled?: boolean;
  /** Toggle Live TV programme-segment analytics (capture + re-admission) on/off. */
  liveTvEnrichmentEnabled?: boolean;
}

/** Connection test result */
export interface ConnectionTestResult {
  presentation?: import("../i18n/streamingFeedbackPresentation").StreamingRemoteFeedback | undefined;
  ok: boolean;
  serverName?: string;
  version?: string;
  error?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param enabled Pass `false` from a surface whose caller may not be an admin.
 *
 *   `/api/streaming/settings` is admin-only and is not one of the routes the
 *   audit log treats as routine, so a non-admin reaching it does not merely get
 *   an ignorable 403 — it writes a warn-level audit row on every page load. A
 *   legitimate user of a granted feature would accumulate a trail that reads
 *   like privilege probing, which is both noise in a security surface and a way
 *   to get someone flagged for doing nothing wrong.
 *
 *   Gating the result is not enough; the request must not be made. Defaults to
 *   `true` so every existing caller is unchanged.
 */
export const useWSDSettings = (enabled: boolean = true) => {
  const [settings, setSettings] = useState<WSDSettingsDTO | null>(null);
  // Starts settled when disabled: a caller that will never fetch must not be
  // told it is loading forever.
  const [loading, setLoading] = useState(enabled);
  const [errorFeedback, setError] = useState<string | StreamingMessage | null>(null);
  useStreamingLanguage();
  const error = streamMessage(errorFeedback);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<MediaServerType | null>(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // Fetch Settings
  // ─────────────────────────────────────────────────────────────────────────────

  const fetchSettings = useCallback(async () => {
    // Before anything else, including the loading flag: the point is that no
    // request leaves the browser, and `refresh()` must be inert too.
    if (!enabled) return;

    try {
      setLoading(true);
      setError(null);

      const res = await fetch("/api/streaming/settings", {
        headers: { Accept: "application/json" },
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        setError((streamingRemoteFeedback(data) ?? data.message ?? streamLocal("failedToLoadStreamingSettings")));
        return;
      }

      setSettings(data.data);
    } catch {
      setError(streamLocal("failedToLoadStreamingSettings"));
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void fetchSettings();
  }, [fetchSettings]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Update Settings
  // ─────────────────────────────────────────────────────────────────────────────

  const updateSettings = useCallback(
    async (update: WSDSettingsUpdate): Promise<boolean> => {
      try {
        setSaving(true);
        const { ok, body: data } = await putStreamingSettings(update, {
          csrfToken: getCsrfToken(),
          afterSave: invalidateSystemAppsCache,
        });

        if (!ok) {
          toast.error(formatSettingsError(data, streamText("failedToUpdateSettings")));
          return false;
        }

        setSettings((data.data as WSDSettingsDTO | undefined) ?? null);
        toast.success(streamText("streamingSettingsSaved"));
        return true;
      } catch {
        toast.error(streamText("failedToUpdateSettings"));
        return false;
      } finally {
        setSaving(false);
      }
    },
    []
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Test Connection
  // ─────────────────────────────────────────────────────────────────────────────

  const testConnection = useCallback(
    async (
      type: MediaServerType,
      url: string,
      apiKey: string
    ): Promise<ConnectionTestResult> => {
      try {
        setTesting(type);
        const csrfToken = getCsrfToken();

        const res = await fetch("/api/streaming/test-connection", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
          },
          body: JSON.stringify({ type, url, apiKey }),
        });

        const data = await res.json();

        // Check both the HTTP status and envelope ok flag.
        // Also check the inner data.ok in case the backend wraps the result.
        const innerResult = data.data as ConnectionTestResult | undefined;
        const succeeded = res.ok && data.ok && (innerResult?.ok !== false);

        if (!succeeded) {
          const rawError = data.message ?? innerResult?.error ?? streamText("connectionTestFailed");
          const errorMsg = streamingResponseText(data.message ? data : innerResult, rawError);
          const result: ConnectionTestResult = {
            ok: false,
            error: rawError,
            presentation: streamingRemoteFeedback(data.message ? data : innerResult),
          };
          toast.error(errorMsg);
          return result;
        }

        const result: ConnectionTestResult = {
          ok: true,
          serverName: innerResult?.serverName,
          version: innerResult?.version,
        };

        const serverLabel = getServerLabel(type);
        toast.success(streamText("connectedToServerLabelServerName", { serverLabel: serverLabel, serverName: result.serverName ? ` (${result.serverName})` : "" }));
        return result;
      } catch {
        const result: ConnectionTestResult = {
          ok: false,
          error: streamText("connectionTestFailed"),
        };
        toast.error(result.error ?? streamText("connectionTestFailed"));
        return result;
      } finally {
        setTesting(null);
      }
    },
    []
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Return
  // ─────────────────────────────────────────────────────────────────────────────

  return {
    settings,
    loading,
    error,
    saving,
    testing,
    refresh: fetchSettings,
    updateSettings,
    testConnection,
  };
};
